import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { dateKey } from "@/lib/calendar-grid";
import type {
  ContactSearchResult,
  CreateCustomerInput,
  CreateTeamMemberInput,
  CustomerUnified,
  InvoiceAgingBucket,
  MediaSubmission,
  Proposal,
  ProposalInsert,
  ResolveContactFieldInput,
  UpdateContactInput,
  UpdateCustomerInput,
  UpdateTeamMemberInput,
} from "@adva/types";

interface PaginatedResponse<T> {
  total: number;
  limit: number;
  offset: number;
  [key: string]: T[] | number;
}

interface ListParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  warranty_status?: string;
  lifecycle_status?: string;
  role?: string;
  role_type?: string;
  category?: string;
  is_active?: boolean | string;
  scope?: string;
  deal_id?: string;
  opportunity_id?: string;
  customer_role_id?: string;
  discount_type?: string;
  invoice_type?: string;
  aging_bucket?: InvoiceAgingBucket;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  date_from?: string;
  date_to?: string;
  // ADV-1145: transactions filters (opportunity_id already declared above)
  transaction_type?: string;
  payment_type?: string;
  is_approved?: boolean | string;
  invoice_id?: string;
  job_id?: string;
  // ADV-1171: claims-by-obligation (Claims tab on /warranties/:id)
  warranty_id?: string;
  // ADV-1176: service-agreement register filters (ADR-0048)
  service_plan_id?: string;
  // ADV-782: territory filter (crews, jobs, locations)
  territory_id?: string;
  // ADV-1350: jobs calendar / list multi-select filters
  crew_id?: string;
  salesperson_user_id?: string;
  // ADV-1362: opportunities salesperson facet (assigned_rep contact_role id, CSV)
  assigned_rep_id?: string;
}

function buildQuery(params: ListParams): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== "all") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

// Customers
export function useCustomers(params: ListParams = {}) {
  return useQuery({
    queryKey: ["customers", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { customers: unknown[] }>(
        `/core/customers${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: ["customer", id],
    queryFn: () => api.get<Record<string, unknown>>(`/core/customers/${id}`),
    enabled: !!id,
  });
}

// ADV-787: customer create/update mutations powering the /customers/new and
// /customers/:id/edit pages. Mirror the useCreateProposal / useUpdateContact
// precedents (invalidate the list + per-record query on success).
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) =>
      api.post<{ customer: CustomerUnified }>("/core/customers", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
    onError: (error) => logMutationError("useCreateCustomer", error),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateCustomerInput }) =>
      api.patch<{ customer: CustomerUnified }>(
        `/core/customers/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["customer", id] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (error) => logMutationError("useUpdateCustomer", error),
  });
}

// Contacts (ADV-1146) — read-only list + detail over core_api.contacts_with_roles.
// The list/detail responses are one row per (contact, business, role); detail
// returns an array of all roles the contact holds in the current business.
export function useContacts(params: ListParams = {}) {
  return useQuery({
    queryKey: ["contacts", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { contacts: unknown[] }>(
        `/core/contacts${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ["contact", id],
    queryFn: () =>
      api.get<{ contact: Record<string, unknown>[] }>(`/core/contacts/${id}`),
    enabled: !!id,
  });
}

// ADV-1101: PATCH /core/contacts/:id — used by the dual-display "Fix" sheet.
// Invalidates both the per-contact query and the contacts list so a fixed
// email/phone disappears from any "unresolved" filtered list immediately.
//
// Default `onError` logs to the console so a fire-and-forget caller using
// `.mutate()` (instead of `.mutateAsync()`) doesn't silently swallow a
// server error. The Fix sheet uses `.mutateAsync()` + inline <Alert>, so
// the log is purely a safety net for future callers.
function logMutationError(scope: string, error: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[mutation] ${scope}:`, error);
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateContactInput }) =>
      api.patch<{ contact: Record<string, unknown>[] }>(
        `/core/contacts/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["contact", id] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (error) => logMutationError("useUpdateContact", error),
  });
}

// ADV-1101: POST /core/contacts/:id/resolve-field — the "keep as-is" path.
export function useResolveContactField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: ResolveContactFieldInput }) =>
      api.post<{ contact: Record<string, unknown>[] }>(
        `/core/contacts/${vars.id}/resolve-field`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["contact", id] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (error) => logMutationError("useResolveContactField", error),
  });
}

// Team Members
export function useTeamMembers(params: ListParams = {}) {
  return useQuery({
    queryKey: ["team-members", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { team_members: unknown[] }>(
        `/core/team-members${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useTeamMember(id: string) {
  return useQuery({
    queryKey: ["team-member", id],
    queryFn: () =>
      api.get<{ team_member: Record<string, unknown> }>(
        `/core/team-members/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeamMemberInput) =>
      api.post<{ team_member: Record<string, unknown> }>(
        "/core/team-members",
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team-members"] }),
    onError: (error) => logMutationError("useCreateTeamMember", error),
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateTeamMemberInput }) =>
      api.patch<{ team_member: Record<string, unknown> }>(
        `/core/team-members/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["team-member", id] });
      qc.invalidateQueries({ queryKey: ["team-members"] });
    },
    onError: (error) => logMutationError("useUpdateTeamMember", error),
  });
}

export function useTeamMemberStats(id: string) {
  return useQuery({
    queryKey: ["team-member-stats", id],
    queryFn: () =>
      api.get<{
        total_proposals: number;
        total_jobs: number;
        total_revenue: number;
      }>(`/core/team-members/${id}/stats`),
    enabled: !!id,
  });
}

// Proposals
export function useProposals(params: ListParams = {}) {
  return useQuery({
    queryKey: ["proposals", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { proposals: unknown[] }>(
        `/sales/proposals${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useProposal(id: string) {
  return useQuery({
    queryKey: ["proposal", id],
    queryFn: () => api.get<Record<string, unknown>>(`/sales/proposals/${id}`),
    enabled: !!id,
  });
}

// ADV-793: create-proposal mutation used by the v2 ProposalFormSheet. The
// POST /sales/proposals endpoint wraps its response as { proposal: Proposal }
// — callers extract `.proposal.id` for post-create navigation.
export function useCreateProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProposalInsert) =>
      api.post<{ proposal: Proposal }>("/sales/proposals", body),
    onSuccess: (_data, body) => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      // ADV-1457: a new proposal changes an opportunity's proposal_value, which
      // feeds the board header's per-stage pipeline totals.
      queryClient.invalidateQueries({
        queryKey: ["opportunity-pipeline-summary"],
      });
      // ADV-1459: that same proposal_value also drives the board list cards and
      // the linked opportunity's detail — invalidate both so they don't show a
      // stale value until an unrelated refetch (mirrors useUpdateOpportunity).
      queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      if (body.opportunity_id) {
        queryClient.invalidateQueries({
          queryKey: ["opportunity", body.opportunity_id],
        });
      }
    },
  });
}

// Appointments (ADV-1143)
export function useAppointments(params: ListParams = {}) {
  return useQuery({
    queryKey: ["appointments", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { appointments: unknown[] }>(
        `/core/appointments${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useAppointment(id: string) {
  return useQuery({
    queryKey: ["appointment", id],
    queryFn: () => api.get<Record<string, unknown>>(`/core/appointments/${id}`),
    enabled: !!id,
  });
}

// Jobs
export function useJobs(params: ListParams = {}) {
  return useQuery({
    queryKey: ["jobs", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { jobs: unknown[] }>(
        `/ops/jobs${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: ["job", id],
    queryFn: () => api.get<Record<string, unknown>>(`/ops/jobs/${id}`),
    enabled: !!id,
  });
}

// Media (job-site photos)
export function useMediaForEntity(
  entityType: string,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: ["media", "by-entity", entityType, entityId],
    queryFn: () =>
      api.get<{ submissions: MediaSubmission[]; total: number }>(
        `/ops/media/by-entity/${entityType}/${entityId}`,
      ),
    enabled: !!entityType && !!entityId,
  });
}

/**
 * Opportunity gallery data — rolls media up across the opportunity AND its
 * media-associable children (jobs, proposals, warranties…). Job-site photos
 * are linked at the job grain, so a plain `by-entity/opportunity/{id}` would
 * miss them; this route wraps `listMediaForOpportunityRollup` (ADV-125).
 */
export function useMediaForOpportunityRollup(
  opportunityId: string | undefined,
) {
  return useQuery({
    queryKey: ["media", "by-opportunity-rollup", opportunityId],
    queryFn: () =>
      api.get<{ submissions: MediaSubmission[]; total: number }>(
        `/ops/media/by-opportunity-rollup/${opportunityId}`,
      ),
    enabled: !!opportunityId,
  });
}

/**
 * Picks the right media query for an entity surface: opportunities use the
 * cross-child rollup; everything else uses the direct by-entity association.
 * Both underlying queries are always called (rules of hooks) but only the
 * selected one is `enabled`, so exactly one request is in flight.
 */
export function useEntityMedia(
  entityType: "job" | "opportunity",
  entityId: string | undefined,
) {
  const isOpp = entityType === "opportunity";
  const byEntity = useMediaForEntity(
    isOpp ? "" : entityType,
    isOpp ? undefined : entityId,
  );
  const rollup = useMediaForOpportunityRollup(isOpp ? entityId : undefined);
  return isOpp ? rollup : byEntity;
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ job: Record<string, unknown> }>("/ops/jobs", input),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      // ADV-1459: a job feeds the parent opportunity's job_revenue (board list
      // cards + per-stage pipeline totals); invalidate its linked parent keys,
      // and the opportunity detail when the job is created against one.
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["opportunity-pipeline-summary"] });
      const opportunityId = input.opportunity_id;
      if (typeof opportunityId === "string" && opportunityId) {
        qc.invalidateQueries({ queryKey: ["opportunity", opportunityId] });
      }
    },
    onError: (error) => logMutationError("useCreateJob", error),
  });
}

export function useUpdateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: Record<string, unknown> }) =>
      api.patch<{ job: Record<string, unknown> }>(
        `/ops/jobs/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (error) => logMutationError("useUpdateJob", error),
  });
}

export function useProposalItems(proposalId: string) {
  return useQuery({
    queryKey: ["proposal-items", proposalId],
    queryFn: () =>
      api.get<{ items: Record<string, unknown>[] }>(
        `/sales/proposals/${proposalId}/items`,
      ),
    enabled: !!proposalId,
  });
}

export function useCreateCrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ crew: Record<string, unknown> }>("/ops/crews", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crews"] }),
    onError: (error) => logMutationError("useCreateCrew", error),
  });
}

export function useCrew(id: string) {
  return useQuery({
    queryKey: ["crew", id],
    queryFn: () =>
      api.get<{ crew: Record<string, unknown> }>(`/ops/crews/${id}`),
    enabled: !!id,
  });
}

export function useUpdateCrew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: Record<string, unknown> }) =>
      api.patch<{ crew: Record<string, unknown> }>(
        `/ops/crews/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["crew", id] });
      qc.invalidateQueries({ queryKey: ["crews"] });
    },
    onError: (error) => logMutationError("useUpdateCrew", error),
  });
}

// Crews (ADV-779)
export function useCrews(params: ListParams = {}) {
  return useQuery({
    queryKey: ["crews", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { crews: unknown[] }>(
        `/ops/crews${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

// Products
export function useProducts(params: ListParams = {}) {
  return useQuery({
    queryKey: ["products", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { products: unknown[] }>(
        `/core/products${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () =>
      api.get<{ product: Record<string, unknown> }>(`/core/products/${id}`),
    enabled: !!id,
  });
}

// Locations
export function useLocations(params: ListParams = {}) {
  return useQuery({
    queryKey: ["locations", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { locations: unknown[] }>(
        `/core/locations${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useLocation(id: string) {
  return useQuery({
    queryKey: ["location", id],
    queryFn: () =>
      api.get<{ location: Record<string, unknown> }>(`/core/locations/${id}`),
    enabled: !!id,
  });
}

// Territories — endpoint returns the full set unpaginated, ordered by name.
export function useTerritories() {
  return useQuery({
    queryKey: ["territories"],
    queryFn: () => api.get<{ territories: unknown[] }>(`/core/territories`),
  });
}

export function useTerritory(id: string) {
  return useQuery({
    queryKey: ["territory", id],
    queryFn: () =>
      api.get<{ territory: Record<string, unknown> }>(
        `/core/territories/${id}`,
      ),
    enabled: !!id,
  });
}

// Invoices
export function useInvoice(id: string) {
  return useQuery({
    queryKey: ["invoice", id],
    queryFn: () =>
      api.get<{ invoice: Record<string, unknown> }>(`/finance/invoices/${id}`),
    enabled: !!id,
  });
}

export function useInvoices(
  params: ListParams = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["invoices", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { invoices: unknown[] }>(
        `/finance/invoices${buildQuery({ limit: 50, ...params })}`,
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ invoice: Record<string, unknown> }>(
        "/finance/invoices",
        input,
      ),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      // ADV-1459: an invoice feeds the parent opportunity's invoiced_amount
      // (board list cards + per-stage pipeline totals); invalidate its linked
      // parent keys, and the opportunity detail when the invoice targets one.
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["opportunity-pipeline-summary"] });
      const opportunityId = input.opportunity_id;
      if (typeof opportunityId === "string" && opportunityId) {
        qc.invalidateQueries({ queryKey: ["opportunity", opportunityId] });
      }
    },
    onError: (error) => logMutationError("useCreateInvoice", error),
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: Record<string, unknown> }) =>
      api.patch<{ invoice: Record<string, unknown> }>(
        `/finance/invoices/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (error) => logMutationError("useUpdateInvoice", error),
  });
}

// ADV-788: AR Aging summary for the v2 Invoices list page.
export interface InvoiceAgingSummaryRow {
  bucket: InvoiceAgingBucket;
  invoice_count: number;
  total_balance: number;
}

export function useInvoiceAgingSummary() {
  return useQuery({
    queryKey: ["invoices", "aging-summary"],
    queryFn: () =>
      api.get<{ buckets: InvoiceAgingSummaryRow[] }>(
        `/finance/invoices/aging-summary`,
      ),
  });
}

// Transactions (ADV-1145) — financial ledger list + detail (Pattern C)
export function useTransactions(
  params: ListParams = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["transactions", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { transactions: unknown[] }>(
        `/finance/transactions${buildQuery({ limit: 50, ...params })}`,
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useTransaction(id: string) {
  return useQuery({
    queryKey: ["transaction", id],
    queryFn: () =>
      api.get<Record<string, unknown>>(`/finance/transactions/${id}`),
    enabled: !!id,
  });
}

// Warranty 3-layer model — ADV-1170 / ADR-0047
//   useWarrantyTerms / useWarrantyTerm   → templates (finance.warranty_terms)
//   useWarranties / useWarranty          → obligations (finance.warranties — RECLAIMED name)
//   useWarrantyClaims / useWarrantyClaim → remediation work (finance.warranty_claims, was ADV-1144)

export function useWarrantyTerms(params: ListParams = {}) {
  return useQuery({
    queryKey: ["warranty-terms", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { warranty_terms: unknown[] }>(
        `/finance/warranty-terms${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useWarrantyTerm(id: string) {
  return useQuery({
    queryKey: ["warranty-term", id],
    queryFn: () =>
      api.get<{ warranty_term: Record<string, unknown> }>(
        `/finance/warranty-terms/${id}`,
      ),
    enabled: !!id,
  });
}

export function useWarranties(params: ListParams = {}) {
  return useQuery({
    queryKey: ["warranties", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { warranties: unknown[] }>(
        `/finance/warranties${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useWarranty(id: string) {
  return useQuery({
    queryKey: ["warranty", id],
    queryFn: () =>
      api.get<{ warranty: Record<string, unknown> }>(
        `/finance/warranties/${id}`,
      ),
    enabled: !!id,
  });
}

export function useWarrantyClaims(params: ListParams = {}) {
  return useQuery({
    queryKey: ["warranty-claims", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { warranty_claims: unknown[] }>(
        `/finance/warranty-claims${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useWarrantyClaim(id: string) {
  return useQuery({
    queryKey: ["warranty-claim", id],
    queryFn: () =>
      api.get<{ warranty_claim: Record<string, unknown> }>(
        `/finance/warranty-claims/${id}`,
      ),
    enabled: !!id,
  });
}

// Service / maintenance agreements — ADV-1176 / ADR-0048
//   useServicePlans / useServicePlan           → templates (finance.service_plans)
//   useServiceAgreements / useServiceAgreement → instances (finance.service_agreements)
// Read-only surfaces, mirroring the warranty register (write-UI deferred). The
// agreement read carries DB-computed recurring dates plus display enrichment
// (customer_name, plan_name, AGR-<id6> display_id).

export function useServicePlans(params: ListParams = {}) {
  return useQuery({
    queryKey: ["service-plans", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { service_plans: unknown[] }>(
        `/finance/service-plans${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useServicePlan(id: string) {
  return useQuery({
    queryKey: ["service-plan", id],
    queryFn: () =>
      api.get<{ service_plan: Record<string, unknown> }>(
        `/finance/service-plans/${id}`,
      ),
    enabled: !!id,
  });
}

export function useServiceAgreements(params: ListParams = {}) {
  return useQuery({
    queryKey: ["service-agreements", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { service_agreements: unknown[] }>(
        `/finance/service-agreements${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useServiceAgreement(id: string) {
  return useQuery({
    queryKey: ["service-agreement", id],
    queryFn: () =>
      api.get<{ service_agreement: Record<string, unknown> }>(
        `/finance/service-agreements/${id}`,
      ),
    enabled: !!id,
  });
}

// Commissions (ADV-1147) — read-only detail; list page lands via ADV-797
export function useCommission(id: string) {
  return useQuery({
    queryKey: ["commission", id],
    queryFn: () =>
      api.get<{ commission: Record<string, unknown> }>(
        `/finance/commissions/${id}`,
      ),
    enabled: !!id,
  });
}

// ADV-799: Today page hooks ─────────────────────────────────────────────────

// Local `YYYY-MM-DD` for "today". Must use local components (via `dateKey`),
// NOT `toISOString()` (UTC): the jobs calendar buckets on local date, so a UTC
// key shifts a US user (UTC−5..−8) after ~4–7pm local onto tomorrow, hiding
// today's jobs and showing tomorrow's. Mirrors the local-date handling in
// `dateKey` and `formatDateOnly` (ADV-1458).
export function todayIso(): string {
  return dateKey(new Date());
}

/** Today's Jobs — thin wrapper over /ops/jobs?date_from=today&date_to=today. */
export function useTodaysJobs() {
  const today = todayIso();
  return useJobs({ date_from: today, date_to: today });
}

interface FollowUpsResponse {
  follow_ups: Record<string, unknown>[];
  total: number;
}
export function useFollowUps(limit = 25) {
  return useQuery({
    queryKey: ["follow-ups", limit],
    queryFn: () =>
      api.get<FollowUpsResponse>(`/core/follow-ups?limit=${limit}`),
  });
}

interface ActivityEventFeedResponse {
  activity_events: Record<string, unknown>[];
}
export function useActivityEventFeed(limit = 20) {
  return useQuery({
    queryKey: ["activity-event-feed", limit],
    queryFn: () =>
      api.get<ActivityEventFeedResponse>(
        `/core/activity-events/feed?limit=${limit}`,
      ),
  });
}

interface CommissionSummaryResponse {
  summary: {
    pay_period_start: string | null;
    pay_period_end: string | null;
    pay_date: string | null;
    pending_amount: number;
    payable_amount: number;
    paid_amount: number;
    total_amount: number;
    count: number;
  };
}
export function useCommissionSummary() {
  return useQuery({
    queryKey: ["commission-summary"],
    queryFn: () =>
      api.get<CommissionSummaryResponse>(`/finance/commissions/summary`),
  });
}

// Opportunities (canonical per ADR-0023; `deal_type` query param kept — DB column name)
export function useOpportunities(
  params: ListParams & { stage?: string; deal_type?: string } = {},
) {
  return useQuery({
    queryKey: ["opportunities", params],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { opportunities: unknown[] }>(
        `/core/opportunities${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

export function useOpportunity(id: string) {
  return useQuery({
    queryKey: ["opportunity", id],
    queryFn: () =>
      api.get<Record<string, unknown>>(`/core/opportunities/${id}`),
    enabled: !!id,
  });
}

// ADV-1457: DB-computed per-stage pipeline aggregate. The board header consumes
// `total_proposal_value` from here instead of summing one capped page of rows
// client-side (which silently undercounts beyond `limit`). `assigned_rep_id`
// (CSV) mirrors the board's salesperson facet so the header matches the cards.
export interface OpportunityPipelineStageRow {
  stage: string;
  opportunity_count: number;
  total_proposal_value: number;
  total_job_revenue: number;
  total_invoiced_amount: number;
}
export interface OpportunityPipelineSummary {
  stages: OpportunityPipelineStageRow[];
  // ADV-1481: DB-computed active-pipeline grand total (non-terminal proposal
  // value), scoped identically to `stages`. The dashboard Pipeline KPI reads
  // this scalar instead of reducing per-stage rows client-side.
  active_pipeline_value: number;
}
export function useOpportunityPipelineSummary(
  params: Pick<ListParams, "assigned_rep_id"> = {},
) {
  return useQuery({
    queryKey: ["opportunity-pipeline-summary", params],
    queryFn: () =>
      api.get<OpportunityPipelineSummary>(
        `/core/opportunities/pipeline-summary${buildQuery(params)}`,
      ),
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<{ opportunity: Record<string, unknown> }>(
        "/core/opportunities",
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      // ADV-1457: the board header's per-stage totals come from this query.
      qc.invalidateQueries({ queryKey: ["opportunity-pipeline-summary"] });
    },
    onError: (error) => logMutationError("useCreateOpportunity", error),
  });
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: Record<string, unknown> }) =>
      api.patch<{ opportunity: Record<string, unknown> }>(
        `/core/opportunities/${vars.id}`,
        vars.input,
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["opportunity", id] });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      // ADV-1457: a stage/value change shifts the board header's per-stage totals.
      qc.invalidateQueries({ queryKey: ["opportunity-pipeline-summary"] });
    },
    onError: (error) => logMutationError("useUpdateOpportunity", error),
  });
}

// Customer related entities (proposals, jobs, invoices, locations)
export function useCustomerRelated(contactRoleId?: string) {
  return useQuery({
    queryKey: ["customer-related", contactRoleId],
    queryFn: () =>
      api.get<{
        proposals: Record<string, unknown>[];
        jobs: Record<string, unknown>[];
        invoices: Record<string, unknown>[];
        locations: Record<string, unknown>[];
        opportunities: Record<string, unknown>[];
      }>(`/core/customers/${contactRoleId}/related`),
    enabled: !!contactRoleId,
  });
}

// Global contact search (account-scoped, deduped)
export function useSearchContacts(q: string) {
  return useQuery({
    queryKey: ["search-contacts", q],
    queryFn: () => {
      const params = new URLSearchParams({ q, limit: "20" });
      return api.get<{ results: ContactSearchResult[] }>(
        `/core/search/contacts?${params.toString()}`,
      );
    },
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

// Per-entity record search hooks for the ⌘K palette (ADV-805). Each calls
// the entity's existing list endpoint with a `search` query param + the
// standard tenant scope applied at the API layer. Replaced by the unified
// /search endpoint when ADV-801 ships.

export function useSearchCustomers(q: string) {
  return useQuery({
    queryKey: ["search-customers", q],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { customers: unknown[] }>(
        `/core/customers${buildQuery({ search: q, limit: 10 })}`,
      ),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useSearchOpportunities(q: string) {
  return useQuery({
    queryKey: ["search-opportunities", q],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { opportunities: unknown[] }>(
        `/core/opportunities${buildQuery({ search: q, limit: 10 })}`,
      ),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useSearchProducts(q: string) {
  return useQuery({
    queryKey: ["search-products", q],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { products: unknown[] }>(
        `/core/products${buildQuery({ search: q, limit: 10 })}`,
      ),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useSearchCrews(q: string) {
  return useQuery({
    queryKey: ["search-crews", q],
    queryFn: () =>
      api.get<PaginatedResponse<unknown> & { crews: unknown[] }>(
        `/ops/crews${buildQuery({ search: q, limit: 10 })}`,
      ),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}
